import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GET, POST } from '../../config/api';
import { getTokens, setTokens } from '../../helpers/tokenStorage';
import { jwtDecode } from 'jwt-decode';
import SettingsIcon from '@mui/icons-material/Settings';
import lightningImage from '../../assets/images/lightning.png';
import styles from './WebsiteAnimatedSplashScreenMobile.module.css';

const WebsiteAnimatedSplashScreenMobile = () => {
  const [blockAppReason, setBlockAppReason] = useState('');
  const navigate = useNavigate();

  // This function is used to load the storage and connect to the socket after the user is logged in
  const loadStorageAndConnectSocket = (
    data,
    token
  ) => {
    // Block the app if the maintenance mode is on
    if (data?.configs?.maintenance_mode) {
      setBlockAppReason(data?.configs?.maintenance_mode ? 'Sorry, We are under maintenance.' : '');
      return;
    }

    // Store configs, categories, and user data in localStorage
    if (data.configs) {
      localStorage.setItem('tellmiz_configs', JSON.stringify(data.configs));
    }
    if (data.categories) {
      localStorage.setItem('tellmiz_categories', JSON.stringify(data.categories));
    }
    if (data.user) {
      const userData = { ...data.user };
      delete userData.chats; // Remove chats as it's stored separately
      localStorage.setItem('tellmiz_user', JSON.stringify(userData));
      if (data.user.unreadMessagesCount !== undefined) {
        localStorage.setItem('tellmiz_unreadMessagesCount', data.user.unreadMessagesCount.toString());
      }
      if (data.user.chats) {
        localStorage.setItem('tellmiz_chats', JSON.stringify(data.user.chats));
      }
    }

    // Store token in localStorage (already done, but ensure it's set)
    localStorage.setItem('tellmiz_access_token', token);

    // Navigate to reels screen after delay
    setTimeout(() => {
      navigate('/reels');
    }, 1500);
  };

  const handleUserNotLoggedIn = async () => {
    const response = await GET('/auth/configs');
    if (response.status === 200 && response.data) {
      const { data } = response;
      if (data.configs) {
        localStorage.setItem('tellmiz_configs', JSON.stringify(data.configs));
      }
      if (data?.configs?.maintenance_mode) {
        setBlockAppReason('We are under maintenance.');
      } else {
        setTimeout(() => {
          navigate('/landing');
        }, 1500);
      }
    } else if (response.error && response.status) {
      setBlockAppReason('We are under maintenance.');
    } else if (response.networkError) {
      setBlockAppReason('Something went wrong. Please try again later.');
    } else {
      setBlockAppReason('Something went wrong. Please try again later.');
    }
  };

  // This useEffect is used to check if the user is logged in and if the token is valid
  useEffect(() => {
    async function prepare() {
      try {
        let [localToken, localRefreshToken] = await getTokens();
        if (localToken && localRefreshToken && localToken?.trim() !== '' && localRefreshToken?.trim() !== '') {
          const { exp } = jwtDecode(localToken);
          const currentTime = Math.floor(Date.now() / 1000);
          if (currentTime < exp - 3600) {
            const response = await POST('/auth/validateAccessToken', {}, { accessToken: localToken });
            if (response.status === 200 && response.data) {
              loadStorageAndConnectSocket(response.data, localToken);
            } else if (response.error) {
              if (response.status === 500) {
                setBlockAppReason('We are under maintenance.');
              } else if (response.status === 401) {
                setTimeout(() => {
                  navigate('/landing');
                }, 1500);
                return;
              } else {
                setBlockAppReason('We are under maintenance.');
              }
            } else {
              setBlockAppReason('We are under maintenance.');
            }
          } else {
            const response = await POST('/auth/refreshTokens', {}, {
              refreshToken: localRefreshToken,
              onBoot: true,
            });
            if ((response.status === 200 || response.status === 201) && response.data) {
              const { accessToken, refreshToken } = response.data;
              if (refreshToken) {
                localRefreshToken = refreshToken;
              }
              await setTokens(accessToken, localRefreshToken);
              loadStorageAndConnectSocket(response.data, accessToken);
            } else if (response.error) {
              if (response.status === 401) {
                setTimeout(() => {
                  navigate('/landing');
                }, 1500);
              } else {
                setBlockAppReason('We are under maintenance.');
              }
            } else {
              setBlockAppReason('We are under maintenance.');
            }
          }
        } else {
          await handleUserNotLoggedIn();
        }
      } catch (error) {
        console.error('Error during app initialization:', error);
        await handleUserNotLoggedIn();
      }
    }

    prepare();
  }, [navigate]);

  // Function to restart the app by clearing the block state and re-running initialization
  const handleTryAgain = async () => {
    setBlockAppReason('');
    try {
      let [localToken, localRefreshToken] = await getTokens();
      if (localToken && localRefreshToken && localToken?.trim() !== '' && localRefreshToken?.trim() !== '') {
        const { exp } = jwtDecode(localToken);
        const currentTime = Math.floor(Date.now() / 1000);
        if (currentTime < exp - 3600) {
          const response = await POST('/auth/validateAccessToken', {}, { accessToken: localToken });
          if (response.status === 200 && response.data) {
            loadStorageAndConnectSocket(response.data, localToken);
          } else if (response.error) {
            if (response.status === 500) {
              setBlockAppReason('We are under maintenance.');
            } else if (response.status === 401) {
              setTimeout(() => {
                navigate('/landing');
              }, 1500);
              return;
            } else {
              setBlockAppReason('We are under maintenance.');
            }
          } else {
            setBlockAppReason('We are under maintenance.');
          }
        } else {
          const response = await POST('/auth/refreshTokens', {}, {
            refreshToken: localRefreshToken,
            onBoot: true,
          });
          if ((response.status === 200 || response.status === 201) && response.data) {
            const { accessToken, refreshToken } = response.data;
            if (refreshToken) {
              localRefreshToken = refreshToken;
            }
            await setTokens(accessToken, localRefreshToken);
            loadStorageAndConnectSocket(response.data, accessToken);
          } else if (response.error) {
            if (response.status === 401) {
              setTimeout(() => {
                navigate('/landing');
              }, 1500);
            } else {
              setBlockAppReason('We are under maintenance.');
            }
          } else {
            setBlockAppReason('We are under maintenance.');
          }
        }
      } else {
        await handleUserNotLoggedIn();
      }
    } catch (error) {
      console.error('Error during app restart:', error);
      await handleUserNotLoggedIn();
    }
  };

  if (blockAppReason) {
    return (
      <div className={styles.blockContainer}>
        <div className={styles.blockGradient}></div>
        <div className={styles.blockContent}>
          <div className={styles.blockIconContainer}>
            <SettingsIcon className={styles.blockIcon} />
          </div>
          <div className={styles.blockTextContainer}>
            <h1 className={styles.blockTitle}>We'll be right back</h1>
            <p className={styles.blockSubMessage}>Thank you for your patience</p>
          </div>
          <button className={styles.tryAgainButton} onClick={handleTryAgain}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.gradientBackground}></div>
      <div className={styles.gradientOverlay}></div>
      <div className={styles.content}>
        <div className={styles.logoContainer}>
          <img src={lightningImage} alt="Lightning" className={styles.logo} />
        </div>
        <div className={styles.landingHead}>
          <div className={styles.titleContainer}>
            {Array.from('TELLMIZ').map((letter, index) => (
              <span key={index} className={styles.letter} style={{ animationDelay: `${500 + index * 80}ms` }}>
                {letter}
              </span>
            ))}
          </div>
          <p className={styles.subtitle}>Let's Do This ⚡️</p>
        </div>
      </div>
    </div>
  );
};

export default WebsiteAnimatedSplashScreenMobile;
